import { useCallback, useEffect, useState } from 'react';
import styles from './AgentList.module.css';
import { getPage } from '../../helpers/api';
import { firstChineseChar } from '../../helpers/firstChineseChar';
import { ProxyChartModal } from '../ProxyChartModal/ProxyChartModal';
import { RechargeModal } from '../RechargeModal/RechargeModal';
import { RechargeLogModal } from '../RechargeLogModal/RechargeLogModal';

type ProxyActivationDetail = {
  proxyId: number;
  proxyName: string;
  createdAt: string;
  todayCount: number;
  yesterdayCount: number;
  weekCount: number;
  monthCount: number;
  recycleToday: number;
  recycleYesterday: number;
  recycleWeek: number;
  recycleMonth: number;
  balance: number;
};

const PAGE_SIZE = 6;
const GAME_COLORS = ['blue', 'purple', 'orange', 'red', 'cyan'];

const format = (n: number) => Number(n || 0).toLocaleString();

const isToday = (value?: string) => {
  if (!value) {
    return false;
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return false;
  }
  const now = new Date();
  return (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  );
};

const getRowTone = (agent: { createdAt: string }) =>
  isToday(agent.createdAt) ? 'rowNew' : '';

const RobotIcon = () => (
  <svg viewBox="0 0 24 24">
    <rect x="4" y="7" width="16" height="12" rx="3" />
    <path d="M12 3v4" />
    <circle cx="12" cy="2.5" r="1" />
    <circle cx="9" cy="13" r="1" />
    <circle cx="15" cy="13" r="1" />
    <path d="M9 16h6" />
  </svg>
);

export const AgentList = () => {
  const [agents, setAgents] = useState<ProxyActivationDetail[]>([]);
  const [page, setPage] = useState(1);
  const [pages, setPages] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingDir, setLoadingDir] = useState<'prev' | 'next' | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [selectedProxy, setSelectedProxy] = useState<ProxyActivationDetail | null>(null);
  const [selectedPeriod, setSelectedPeriod] = useState(4);
  const [rechargeProxy, setRechargeProxy] = useState<ProxyActivationDetail | null>(null);
  const [logProxy, setLogProxy] = useState<ProxyActivationDetail | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);
  const [spinning, setSpinning] = useState(false);

  const fetchAgents = useCallback(async (pageNum: number, dir: 'prev' | 'next' | null = null) => {
    setLoading(true);
    setLoadingDir(dir);
    setError(null);
    try {
      const result = await getPage<ProxyActivationDetail>(
        `/admin/tel/proxy/stats?page=${pageNum}&limit=${PAGE_SIZE}`
      );
      setAgents(result.records);
      setPage(result.current);
      setPages(result.pages);
    } catch (e) {
      setError(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
      setLoadingDir(null);
    }
  }, []);

  useEffect(() => {
    fetchAgents(1);
  }, [fetchAgents, refreshKey]);

  const handleRefresh = () => {
    setSpinning(true);
    setRefreshKey((key) => key + 1);
    setTimeout(() => setSpinning(false), 600);
  };

  const openChart = (agent: ProxyActivationDetail, period: number) => {
    setSelectedPeriod(period);
    setSelectedProxy(agent);
  };

  const placeholders = Array.from({ length: Math.max(0, PAGE_SIZE - agents.length) });

  return (
    <div className="card">
      <div className={`card-header ${styles.compactHeader}`}>
        <div className="section-title">
          <div className="icon-box">
            <RobotIcon />
          </div>
          代理统计
        </div>
        <button type="button" className="card-refresh" onClick={handleRefresh}>
          <svg
            className={spinning ? 'card-refresh-spin' : ''}
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
            aria-hidden="true"
          >
            <path d="M21 12a9 9 0 1 1-2.64-6.36" />
            <path d="M21 3v5h-5" />
          </svg>
        </button>
      </div>
      <div className={`${styles.table} ${loading ? styles.loading : ''} ${spinning ? styles.refreshing : ''}`}>
        <div className={styles.tableHead}>
          <span className={styles.center}>代理</span>
          <span className={styles.center}>本月</span>
          <span className={styles.center}>7天</span>
          <span className={styles.center}>昨日</span>
          <span className={styles.center}>今日</span>
        </div>
        {agents.map((agent, index) => (
          <div
            key={agent.proxyId}
            className={`${styles.row} ${styles[getRowTone(agent)] || ''}`}
            onClick={() => openChart(agent, 4)}
          >
            <span className={styles.nameCell}>
              <span
                className={`game-icon circle ${styles.smallIcon} ${styles.clickableAvatar} ${GAME_COLORS[index % GAME_COLORS.length]}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setLogProxy(agent);
                }}
              >
                <span className={styles.letter}>
                  {firstChineseChar(agent.proxyName || '')}
                </span>
              </span>
              <span
                className={`${styles.info} ${styles.clickableInfo}`}
                onClick={(e) => {
                  e.stopPropagation();
                  setLogProxy(agent);
                }}
              >
                <span className={styles.name}>
                  {agent.proxyName || `代理 #${agent.proxyId}`}
                </span>
                <span className={`${styles.balance} ${Number(agent.balance) < 10 ? styles.low : ''}`}>
                  ¥{Number(agent.balance || 0).toLocaleString('zh-CN', {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </span>
              </span>
            </span>
            <span
              className={`${styles.cell} ${styles.clickableCell}`}
              onClick={(e) => {
                e.stopPropagation();
                openChart(agent, 4);
              }}
            >
              <span className={`${styles.num} ${styles.green}`}>{format(agent.monthCount)}</span>
              <span className={styles.meta}>{format(agent.recycleMonth)}</span>
            </span>
            <span
              className={`${styles.cell} ${styles.clickableCell}`}
              onClick={(e) => {
                e.stopPropagation();
                openChart(agent, 3);
              }}
            >
              <span className={`${styles.num} ${styles.purple}`}>{format(agent.weekCount)}</span>
              <span className={styles.meta}>{format(agent.recycleWeek)}</span>
            </span>
            <span
              className={`${styles.cell} ${styles.clickableCell}`}
              onClick={(e) => {
                e.stopPropagation();
                openChart(agent, 2);
              }}
            >
              <span className={`${styles.num} ${styles.orange}`}>{format(agent.yesterdayCount)}</span>
              <span className={styles.meta}>{format(agent.recycleYesterday)}</span>
            </span>
            <span
              className={`${styles.cell} ${styles.clickableCell}`}
              onClick={(e) => {
                e.stopPropagation();
                openChart(agent, 1);
              }}
            >
              <span className={styles.num}>{format(agent.todayCount)}</span>
              <span className={styles.meta}>{format(agent.recycleToday)}</span>
            </span>
          </div>
        ))}
        {placeholders.map((_, index) => (
          <div key={`empty-${index}`} className={styles.row} />
        ))}
      </div>
      {error && <div className="error">{error}</div>}
      {pages > 1 && (
        <div className={`pagination ${styles.paginationTight}`}>
          <button
            type="button"
            className="page-btn prev"
            disabled={loading || page <= 1}
            onClick={() => fetchAgents(page - 1, 'prev')}
          >
            {loading && loadingDir === 'prev' ? <span className="spinner" /> : <span>&lt;</span>}
            上一页
          </button>
          <span className="page-number">{page} / {pages}</span>
          <button
            type="button"
            className="page-btn next"
            disabled={loading || page >= pages}
            onClick={() => fetchAgents(page + 1, 'next')}
          >
            下一页
            {loading && loadingDir === 'next' ? <span className="spinner" /> : <span>&gt;</span>}
          </button>
        </div>
      )}
      <ProxyChartModal
        open={selectedProxy !== null}
        onOpenChange={(next) => {
          if (!next) {
            setSelectedProxy(null);
          }
        }}
        proxyId={selectedProxy?.proxyId ?? 0}
        proxyName={selectedProxy?.proxyName || ''}
        period={selectedPeriod}
      />
      <RechargeModal
        open={rechargeProxy !== null}
        onOpenChange={(next) => {
          if (!next) {
            setRechargeProxy(null);
          }
        }}
        proxyId={rechargeProxy?.proxyId ?? 0}
        proxyName={rechargeProxy?.proxyName || ''}
        balance={rechargeProxy?.balance ?? 0}
        onSuccess={() => fetchAgents(page)}
      />
      <RechargeLogModal
        open={logProxy !== null}
        onOpenChange={(next) => {
          if (!next) {
            setLogProxy(null);
          }
        }}
        proxyId={logProxy?.proxyId ?? 0}
        proxyName={logProxy?.proxyName || ''}
        balance={logProxy?.balance ?? 0}
        onRecharge={() => {
          const target = logProxy;
          setLogProxy(null);
          if (target) {
            setRechargeProxy(target);
          }
        }}
      />
    </div>
  );
};
